import "./quantity.scss";
import { ComponentRegistry } from '@ts/utils/ComponentRegistry';
import { icon } from "@fortawesome/fontawesome-svg-core";
import { faCircleMinus, faCirclePlus } from "@fortawesome/free-solid-svg-icons";

export class Quantity {
  private static registry = new ComponentRegistry<Quantity>('quantity');
  private readonly id: string;
  private readonly input: HTMLInputElement;
  private readonly wrapper: HTMLDivElement;
  private readonly decrementBtn: HTMLButtonElement;
  private readonly incrementBtn: HTMLButtonElement;
  private readonly inputHandler: (e: Event) => void;
  private readonly decrementHandler: () => void;
  private readonly incrementHandler: () => void;
  private readonly minValue: number;
  private readonly maxValue: number | null;

  private constructor(input: HTMLInputElement) {
    if (!(input instanceof HTMLInputElement)) {
      throw new Error('Quantity: Element must be an HTMLInputElement');
    }

    this.id = Quantity.registry.register(input, this);
    this.input = input;
    this.minValue = input.min ? parseInt(input.min, 10) : 1;
    this.maxValue = input.max ? parseInt(input.max, 10) : null;

    const { wrapper, decrementBtn, incrementBtn, inputHandler, decrementHandler, incrementHandler } =
      this.buildQuantityInput(input);

    this.wrapper = wrapper;
    this.decrementBtn = decrementBtn;
    this.incrementBtn = incrementBtn;
    this.inputHandler = inputHandler;
    this.decrementHandler = decrementHandler;
    this.incrementHandler = incrementHandler;

    this.updateButtonStates();
  }

  private buildQuantityInput = (input: HTMLInputElement) => {
    const quantityWrapper = document.createElement('div');
    quantityWrapper.className = 'quantity-wrapper';

    const decrementBtn = document.createElement('button');
    decrementBtn.type = 'button';
    decrementBtn.className = 'quantity-btn quantity-decrement';
    decrementBtn.appendChild(icon(faCircleMinus).node[0]);

    const incrementBtn = document.createElement('button');
    incrementBtn.type = 'button';
    incrementBtn.className = 'quantity-btn quantity-increment';
    incrementBtn.appendChild(icon(faCirclePlus).node[0]);

    input.parentNode?.insertBefore(quantityWrapper, input);
    quantityWrapper.appendChild(decrementBtn);
    quantityWrapper.appendChild(input);
    quantityWrapper.appendChild(incrementBtn);

    const inputHandler = this.createInputHandler(input);

    input.addEventListener('input', inputHandler);
    input.addEventListener('blur', () => this.clampValue(input));

    const decrementHandler = () => {
      const current = parseInt(this.input.value, 10) || this.minValue;
      this.input.value = Math.max(this.minValue, current - 1).toString();
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
      this.updateButtonStates();
    };

  const incrementHandler = () => {
    const current = parseInt(this.input.value, 10) || this.minValue;
    const next = current + 1;
    this.input.value = (this.maxValue !== null ? Math.min(this.maxValue, next) : next).toString();
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    this.updateButtonStates();
  };

    decrementBtn.addEventListener('click', decrementHandler);
    incrementBtn.addEventListener('click', incrementHandler);

    return { wrapper: quantityWrapper, decrementBtn, incrementBtn, inputHandler, decrementHandler, incrementHandler };
  };

  private createInputHandler = (input: HTMLInputElement) => (): void => {
    input.value = input.value.replace(/[^0-9]/g, '');
    const next = parseInt(input.value, 10) || this.minValue;
    this.input.value = (this.maxValue !== null ? Math.min(this.maxValue, next) : next).toString();
    this.updateButtonStates();
  };

  private updateButtonStates = (): void => {
    const current = parseInt(this.input.value, 10) || this.minValue;
    this.decrementBtn.disabled = current <= this.minValue;
    this.incrementBtn.disabled = this.maxValue !== null && current >= this.maxValue;
  };

  public static create(
    target: string | HTMLInputElement | NodeListOf<HTMLElement> | HTMLElement[]
  ): Quantity[] | null {
    const inputs = ComponentRegistry.processTargets<Quantity, HTMLElement>(
            Quantity.registry,
            target,
            [HTMLInputElement]
        ) as HTMLInputElement[];

    if (!inputs.length) return null;
    return inputs.map(input => new Quantity(input));
  }

  public static getQuantity(id: string): Quantity | null {
    return Quantity.registry.get(id) ?? null;
  }

  public destroy(): void {
    this.input.removeEventListener('input', this.inputHandler);
    this.decrementBtn.removeEventListener('click', this.decrementHandler);
    this.incrementBtn.removeEventListener('click', this.incrementHandler);

    this.wrapper.parentNode?.insertBefore(this.input, this.wrapper);
    this.wrapper.remove();

    Quantity.registry.unregister(this.id);
  }

  private clampValue(input: HTMLInputElement): void {
    const current = parseInt(input.value, 10) || this.minValue;
    let clamped = Math.max(this.minValue, current);
    if (this.maxValue !== null) {
        clamped = Math.min(this.maxValue, clamped);
    }
    input.value = clamped.toString();
    this.updateButtonStates();
  }
}